import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { 
  Activity,
  AlertTriangle,
  Wind,
  Heart,
  Thermometer,
  FileText,
  Download,
  RotateCcw,
  Sparkles,
  Loader2,
  Droplet,
  Eye,
  Stethoscope
} from 'lucide-react';

import { API_URL } from '../config';

export default function MARCHEChecklistPage() {
  const { token } = useAuth();
  const printRef = useRef(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('marche-checklist');
      return saved ? JSON.parse(saved) : {
        m: { hemorragieControlee: null },
        a: { vasLibres: null },
        r: {
          ampliationSymetrique: null,
          murmureSymetrique: null,
          emphysemeAbsent: null,
          spo2: null,
          frequenceRespiratoire: null,
          plaieSoufflante: null,
          o2: null,
          iot: null
        },
        c: {
          pas: '',
          pad: '',
          fc: '',
          pam: '',
          shockIndex: ''
        },
        h: {
          hypothermie: null
        },
        e: {
          notes: ''
        }
      };
    } catch {
      return {
        m: { hemorragieControlee: null },
        a: { vasLibres: null },
        r: {
          ampliationSymetrique: null,
          murmureSymetrique: null,
          emphysemeAbsent: null,
          spo2: null,
          frequenceRespiratoire: null,
          plaieSoufflante: null,
          o2: null,
          iot: null
        },
        c: {
          pas: '',
          pad: '',
          fc: '',
          pam: '',
          shockIndex: ''
        },
        h: {
          hypothermie: null
        },
        e: {
          notes: ''
        }
      };
    }
  });

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('marche-checklist', JSON.stringify(formData));
  }, [formData]);

  // Calculate PAM and Shock Index
  useEffect(() => {
    if (formData.c.pas && formData.c.pad) {
      const pas = parseFloat(formData.c.pas);
      const pad = parseFloat(formData.c.pad);
      const pam = Math.round(pad + (pas - pad) / 3);
      if (!isNaN(pam)) {
        setFormData(prev => ({
          ...prev,
          c: { ...prev.c, pam: pam.toString() }
        }));
      }
    }

    if (formData.c.fc && formData.c.pas) {
      const fc = parseFloat(formData.c.fc);
      const pas = parseFloat(formData.c.pas);
      const si = (fc / pas).toFixed(2);
      if (!isNaN(si)) {
        setFormData(prev => ({
          ...prev,
          c: { ...prev.c, shockIndex: si }
        }));
      }
    }
  }, [formData.c.fc, formData.c.pas, formData.c.pad]);

  const handleReset = () => {
    if (window.confirm('Réinitialiser la checklist MARCHE ?')) {
      localStorage.removeItem('marche-checklist');
      window.location.reload();
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Génération du PDF...');
      const opt = {
        margin: 10,
        filename: `Checklist-MARCHE-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(printRef.current).save();
      toast.success('PDF exporté');
    } catch (error) {
      toast.error('Erreur export PDF');
    }
  };

  const handleAnalyzeAI = async () => {
    try {
      setIsAnalyzing(true);
      toast.info('Analyse IA MARCHE en cours...');

      const response = await fetch(`${API_URL}/api/ai/analyze-marche`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Erreur analyse');
      
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('Analyse terminée');
    } catch (error) {
      toast.error('Erreur analyse IA');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const RadioGroup = ({ name, value, onChange, options }) => (
    <div className="flex gap-2">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`px-3 py-1.5 text-xs md:text-sm rounded-lg font-medium transition-all ${
            value === option.value
              ? option.color || 'bg-green-500 text-white'
              : 'bg-[hsl(var(--bg-elev))] text-[hsl(var(--text-dim))] hover:bg-[hsl(var(--bg-elev))]/80'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4 max-w-5xl mx-auto">
      {/* Header */}
      <div className="space-y-2 print:hidden">
        <div className="flex flex-col sm:flex-row justify-between gap-2">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 md:w-8 md:h-8 text-red-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--text))]">Check-list MARCHE</h1>
              <p className="text-xs text-[hsl(var(--text-dim))]">Patient traumatisé adulte en pré-hospitalier</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAnalyzeAI} disabled={isAnalyzing} size="sm" className="bg-purple-400/20 text-purple-300 border-purple-400/30">
              {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </Button>
            <Button onClick={handleReset} size="sm" variant="outline" className="text-amber-300 border-amber-400/30">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button onClick={handleExportPDF} size="sm" className="bg-cyan-400/20 text-cyan-300 border-cyan-400/30">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <Alert className="bg-red-400/10 border-red-400/30 p-2">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          <AlertDescription className="text-xs text-[hsl(var(--text))]">
            <strong>MARCHE :</strong> Évaluation systématique du traumatisé sévère
          </AlertDescription>
        </Alert>
      </div>

      <div ref={printRef} className="space-y-3">
        {/* M - MASSIVE BLEEDING CONTROL */}
        <Card className="border-red-400/30 bg-red-400/5">
          <CardHeader className="p-3 bg-[hsl(var(--bg-elev))]">
            <CardTitle className="text-sm md:text-base text-red-300 flex items-center gap-2">
              <Droplet className="w-4 h-4" />
              <span className="font-mono font-bold">M</span> - Massive bleeding control
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Hémorragie contrôlée</div>
              <RadioGroup
                name="hemorragie"
                value={formData.m.hemorragieControlee}
                onChange={(val) => setFormData(prev => ({ ...prev, m: { hemorragieControlee: val } }))}
                options={[
                  { label: 'oui', value: true, color: 'bg-green-500 text-white' },
                  { label: 'non', value: false, color: 'bg-red-500 text-white' }
                ]}
              />
            </div>
          </CardContent>
        </Card>

        {/* A - AIRWAY */}
        <Card className="border-blue-400/30 bg-blue-400/5">
          <CardHeader className="p-3 bg-[hsl(var(--bg-elev))]">
            <CardTitle className="text-sm md:text-base text-blue-300 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span className="font-mono font-bold">A</span> - Airway
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">VAS libres</div>
              <RadioGroup
                name="vasLibres"
                value={formData.a.vasLibres}
                onChange={(val) => setFormData(prev => ({ ...prev, a: { vasLibres: val } }))}
                options={[
                  { label: 'oui', value: true, color: 'bg-green-500 text-white' },
                  { label: 'non', value: false, color: 'bg-red-500 text-white' }
                ]}
              />
            </div>
          </CardContent>
        </Card>

        {/* R - RESPIRATION */}
        <Card className="border-cyan-400/30 bg-cyan-400/5">
          <CardHeader className="p-3 bg-[hsl(var(--bg-elev))]">
            <CardTitle className="text-sm md:text-base text-cyan-300 flex items-center gap-2">
              <Wind className="w-4 h-4" />
              <span className="font-mono font-bold">R</span> - Respiration
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            {[
              { key: 'ampliationSymetrique', label: 'Ampliation thoracique symétrique' },
              { key: 'murmureSymetrique', label: 'Murmure vésiculaire symétrique' },
              { key: 'emphysemeAbsent', label: 'Emphysème sous cutané absent' }
            ].map(({ key, label }) => (
              <div key={key}>
                <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">{label}</div>
                <RadioGroup
                  name={key}
                  value={formData.r[key]}
                  onChange={(val) => setFormData(prev => ({ ...prev, r: { ...prev.r, [key]: val } }))}
                  options={[
                    { label: 'oui', value: true, color: 'bg-green-500 text-white' },
                    { label: 'non', value: false, color: 'bg-red-500 text-white' }
                  ]}
                />
              </div>
            ))}

            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">SpO₂ spontanée</div>
              <RadioGroup
                name="spo2"
                value={formData.r.spo2}
                onChange={(val) => setFormData(prev => ({ ...prev, r: { ...prev.r, spo2: val } }))}
                options={[
                  { label: '>94%', value: '>94', color: 'bg-green-500 text-white' },
                  { label: '90-94', value: '90-94', color: 'bg-amber-500 text-white' },
                  { label: '<90%', value: '<90', color: 'bg-red-500 text-white' }
                ]}
              />
            </div>

            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Fréquence respiratoire</div>
              <RadioGroup
                name="fr"
                value={formData.r.frequenceRespiratoire}
                onChange={(val) => setFormData(prev => ({ ...prev, r: { ...prev.r, frequenceRespiratoire: val } }))}
                options={[
                  { label: '<10', value: '<10', color: 'bg-red-500 text-white' },
                  { label: '10-30', value: '10-30', color: 'bg-green-500 text-white' },
                  { label: '>30', value: '>30', color: 'bg-amber-500 text-white' }
                ]}
              />
            </div>

            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Plaie thoracique soufflante</div>
              <RadioGroup
                name="plaieSoufflante"
                value={formData.r.plaieSoufflante}
                onChange={(val) => setFormData(prev => ({ ...prev, r: { ...prev.r, plaieSoufflante: val } }))}
                options={[
                  { label: 'non', value: false, color: 'bg-green-500 text-white' },
                  { label: 'oui', value: true, color: 'bg-red-500 text-white' }
                ]}
              />
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[hsl(var(--border))]">
              <div>
                <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">O₂</div>
                <RadioGroup
                  name="o2"
                  value={formData.r.o2}
                  onChange={(val) => setFormData(prev => ({ ...prev, r: { ...prev.r, o2: val } }))}
                  options={[
                    { label: 'fait', value: 'fait', color: 'bg-green-500 text-white' },
                    { label: 'non fait', value: 'non_fait', color: 'bg-amber-500 text-white' },
                    { label: 'N/A', value: 'na', color: 'bg-gray-500 text-white' }
                  ]}
                />
              </div>
              <div>
                <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">IOT</div>
                <RadioGroup
                  name="iot"
                  value={formData.r.iot}
                  onChange={(val) => setFormData(prev => ({ ...prev, r: { ...prev.r, iot: val } }))}
                  options={[
                    { label: 'fait', value: 'fait', color: 'bg-green-500 text-white' },
                    { label: 'non fait', value: 'non_fait', color: 'bg-amber-500 text-white' },
                    { label: 'N/A', value: 'na', color: 'bg-gray-500 text-white' }
                  ]}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* C - CHOC/CIRCULATION */}
        <Card className="border-purple-400/30 bg-purple-400/5">
          <CardHeader className="p-3 bg-[hsl(var(--bg-elev))]">
            <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
              <Heart className="w-4 h-4" />
              <span className="font-mono font-bold">C</span> - Choc / Circulation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-medium text-[hsl(var(--text))] mb-1 block">PAS initiale</label>
                <Input
                  type="number"
                  value={formData.c.pas}
                  onChange={(e) => setFormData(prev => ({ ...prev, c: { ...prev.c, pas: e.target.value } }))}
                  placeholder="mmHg"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--text))] mb-1 block">PAD initiale</label>
                <Input
                  type="number"
                  value={formData.c.pad}
                  onChange={(e) => setFormData(prev => ({ ...prev, c: { ...prev.c, pad: e.target.value } }))}
                  placeholder="mmHg"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--text))] mb-1 block">Fréq. card.</label>
                <Input
                  type="number"
                  value={formData.c.fc}
                  onChange={(e) => setFormData(prev => ({ ...prev, c: { ...prev.c, fc: e.target.value } }))}
                  placeholder="/min"
                  className="h-8 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-[hsl(var(--text))] mb-1 block">PAM</label>
                <Input
                  type="number"
                  value={formData.c.pam}
                  readOnly
                  placeholder="Auto"
                  className="h-8 text-sm bg-[hsl(var(--bg))] font-mono text-cyan-300"
                />
                <p className="text-[10px] text-green-400 mt-0.5">Calculé automatiquement</p>
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--text))] mb-1 block">Shock Index</label>
                <Input
                  type="text"
                  value={formData.c.shockIndex}
                  readOnly
                  placeholder="Auto"
                  className="h-8 text-sm bg-[hsl(var(--bg))] font-mono text-cyan-300"
                />
                <p className="text-[10px] text-green-400 mt-0.5">initial - FC/PAS</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* H - HYPOTHERMIA */}
        <Card className="border-blue-400/30 bg-blue-400/5">
          <CardHeader className="p-3 bg-[hsl(var(--bg-elev))]">
            <CardTitle className="text-sm md:text-base text-blue-300 flex items-center gap-2">
              <Thermometer className="w-4 h-4" />
              <span className="font-mono font-bold">H</span> - Hypothermia / Hypovolemia
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Hypothermie</div>
              <RadioGroup
                name="hypothermie"
                value={formData.h.hypothermie}
                onChange={(val) => setFormData(prev => ({ ...prev, h: { hypothermie: val } }))}
                options={[
                  { label: 'non', value: false, color: 'bg-green-500 text-white' },
                  { label: 'oui', value: true, color: 'bg-amber-500 text-white' }
                ]}
              />
            </div>
          </CardContent>
        </Card>

        {/* E - EVERYTHING ELSE */}
        <Card className="border-[hsl(var(--border))]">
          <CardHeader className="p-3 bg-[hsl(var(--bg-elev))]">
            <CardTitle className="text-sm md:text-base text-cyan-300 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="font-mono font-bold">E</span> - Everything else
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <textarea
              value={formData.e.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, e: { notes: e.target.value } }))}
              placeholder="Autres observations, lésions, traitements..."
              className="w-full min-h-[80px] p-2 rounded-lg bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))] text-xs md:text-sm text-[hsl(var(--text))] resize-none"
            />
          </CardContent>
        </Card>

        {/* AI ANALYSIS */}
        {aiAnalysis && (
          <Card className="border-purple-500/30 bg-purple-500/5">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                ANALYSE IA - Évaluation MARCHE
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-xs md:text-sm text-[hsl(var(--text))]">
                  {aiAnalysis.analysis}
                </div>
              </div>
              <Alert className="mt-3 bg-amber-400/10 border-amber-400/30 p-2">
                <AlertDescription className="text-xs text-amber-300">
                  <strong>Disclaimer :</strong> Aide à la décision clinique
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
